import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { cn } from "@rekati/utils";
import IconDatabase from "~icons/solar/database-bold";
import IconHeart from "~icons/solar/heart-pulse-bold";
import IconHospital from "~icons/solar/hospital-bold";
import IconLock from "~icons/solar/lock-keyhole-bold";
import IconShield from "~icons/solar/shield-check-bold";
import { Sticker } from "./Sticker";

/** The cable's route, in a 1200×260 box: out of the dish, over three hills, into the tower. */
const ROUTE = "M 90 170 C 230 30, 340 250, 500 150 S 760 20, 870 150 S 1040 240, 1110 110";

const PACKETS = [
  { Icon: IconHeart, tone: "bg-paint-pink text-white" },
  { Icon: IconLock, tone: "bg-volt text-volt-foreground" },
  { Icon: IconDatabase, tone: "bg-paint-blue text-white" },
  { Icon: IconShield, tone: "bg-paint-teal text-white" },
  { Icon: IconHospital, tone: "border border-border bg-card text-foreground" },
];
const SPACING = 0.13;

/**
 * "Connecting systems. Securing data.": a flat yellow cable runs from a dish
 * to a tower as you scroll, and the hospital's
 * data rides it as little sealed packets. When the last packet lands, the
 * tower lights up. Decorative; the words are in the section above it.
 */
export function SignalCable({ className }: { className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const route = useRef<SVGPathElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: box, offset: ["start 0.9", "end 0.45"] });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 22, mass: 0.6 });
  const drawn = useTransform(progress, [0, 0.55], reduce ? [1, 1] : [0, 1]);
  const flowOpacity = useTransform(drawn, [0.9, 1], [0, 0.55]);
  const [arrived, setArrived] = useState(Boolean(reduce));

  useMotionValueEvent(progress, "change", (v) => setArrived(reduce ? true : v > 0.97));

  return (
    <div ref={box} className={cn("relative h-[200px] sm:h-[260px]", className)} aria-hidden="true">
      <svg
        viewBox="0 0 1200 260"
        preserveAspectRatio="none"
        className="absolute inset-0 size-full overflow-visible"
        fill="none"
      >
        {/* One flat yellow cable, and the dashes of data that start flowing once it is laid. */}
        <motion.path
          d={ROUTE}
          stroke="var(--color-volt)"
          strokeWidth={8}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          style={{ pathLength: drawn }}
        />
        <motion.path
          d={ROUTE}
          stroke="var(--color-foreground)"
          strokeWidth={2}
          strokeDasharray="2 14"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          className={cn(!reduce && arrived && "[animation:cable-flow_0.9s_linear_infinite]")}
          style={{ opacity: flowOpacity }}
        />
        <path ref={route} d={ROUTE} stroke="none" />
      </svg>

      <div className="absolute bottom-[-6%] left-[-1%] w-[clamp(84px,13vw,170px)]">
        <Sticker name="parabola" />
      </div>
      <motion.div
        className="absolute right-[-1%] bottom-[18%] w-[clamp(64px,9vw,120px)] origin-bottom"
        animate={arrived && !reduce ? { scale: [1, 1.08, 1] } : { scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <Sticker name="tower" />
        {arrived ? (
          <span className="pointer-events-none absolute top-[6%] left-1/2 -translate-x-1/2">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="absolute top-1/2 left-1/2 size-10 rounded-full border-2 border-paint-teal opacity-0 motion-safe:animate-[cable-ping_1.8s_ease-out_infinite]"
                style={{ animationDelay: `${i * 0.45}s` }}
              />
            ))}
          </span>
        ) : null}
      </motion.div>

      {PACKETS.map((p, i) => (
        <Packet key={i} index={i} progress={progress} route={route} box={box} still={Boolean(reduce)}>
          <span className={cn("grid size-9 place-items-center rounded-xl shadow-soft sm:size-11", p.tone)}>
            <p.Icon className="size-4.5 sm:size-5" />
          </span>
        </Packet>
      ))}
    </div>
  );
}

/** One packet riding the cable: its place on the route is the scroll, minus its spot in the queue. */
function Packet({
  index,
  progress,
  route,
  box,
  still,
  children,
}: {
  index: number;
  progress: MotionValue<number>;
  route: React.RefObject<SVGPathElement | null>;
  box: React.RefObject<HTMLDivElement | null>;
  still: boolean;
  children: React.ReactNode;
}) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const tilt = useMotionValue(0);
  const opacity = useMotionValue(still ? 1 : 0);

  useEffect(() => {
    const place = (v: number) => {
      const path = route.current;
      const wrap = box.current;
      const matrix = path?.getScreenCTM();
      if (!path || !wrap || !matrix) return;
      // Packets leave the dish in a queue and stop in a row along the cable's end.
      const t = still
        ? 0.12 + index * 0.17
        : Math.min(1 - index * 0.05, Math.max(0, v * 1.35 - 0.2 - index * SPACING));
      const len = path.getTotalLength();
      const at = path.getPointAtLength(t * len);
      const ahead = path.getPointAtLength(Math.min(len, t * len + 4));
      const rect = wrap.getBoundingClientRect();
      const p = new DOMPoint(at.x, at.y).matrixTransform(matrix);
      const q = new DOMPoint(ahead.x, ahead.y).matrixTransform(matrix);
      x.set(p.x - rect.left);
      y.set(p.y - rect.top);
      // Half the slope: they lean into the hills without tumbling.
      tilt.set((Math.atan2(q.y - p.y, q.x - p.x) * 90) / Math.PI);
      opacity.set(still || t > 0.005 ? 1 : 0);
    };
    place(progress.get());
    const off = progress.on("change", place);
    const wrap = box.current;
    const ro = new ResizeObserver(() => place(progress.get()));
    if (wrap) ro.observe(wrap);
    return () => {
      off();
      ro.disconnect();
    };
  }, [route, box, still, index, progress, x, y, tilt, opacity]);

  return (
    <motion.div className="pointer-events-none absolute top-0 left-0" style={{ x, y, opacity }}>
      <motion.div className="-translate-1/2" style={{ rotate: tilt }}>
        {children}
      </motion.div>
    </motion.div>
  );
}
